// src/pages/Providers.tsx
/**
 * Service Providers Management Page
 * Table with search, pagination, verify/unverify, and toggle active
 * IMPORTANT: Only verified providers can be marked as active
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  ToggleLeft,
  ToggleRight,
  ChevronLeft,
  ChevronRight,
  Star,
  Trash2,
  ChevronDown,
  FileDown,
} from 'lucide-react';
import { Listbox } from '@headlessui/react';

import { realAPI } from '../api/realEndpoints';
import { adaptRealProvider } from '../api/realTypes';
import { useApp } from '../context/AppContext';
import { useToastNotifications } from '../hooks/useToast';
import { debounce, formatDate } from '../utils/helpers';
import { ITEMS_PER_PAGE } from '../utils/constants';
import type { Provider } from '../api/types';
import { getAvatarBackgroundColor, getUserImageSrc, getUserInitial } from '../utils/userImage';
import Card from '../components/Card';
import Button from '../components/Button';
import Modal from '../components/Modal';

/**
 * Local lightweight interfaces to help TS and readability.
 * We keep these minimal to avoid mismatching your shared `Provider` type.
 */
type Review = {
  id: number | string;
  rating: number;
  review?: string;
  createdAt?: string;
  userInfo?: {
    id?: number | string;
    name?: string;
    email?: string;
    image?: string;
    signInProvider?: string[] | null;
  };
};

type Service = {
  id: number | string;
  serviceName?: string;
  description?: string;
  serviceImage?: string;
  price?: number;
  category?: string;
  duration?: number;
};

type ProviderDetail = Partial<Provider> & {
  id: number | string;
  name?: string;
  username?: string | null;
  bio?: string | null;
  countryCode?: string | null;
  mobileNumber?: string | null;
  email?: string | null;
  status?: 'active' | 'inactive' | string;
  image?: string | null;
  signInProvider?: string[] | null;
  averageRating?: number;
  totalRating?: number;
  location?: string | null;
  createdAt?: string | null;
  providerRatings?: Review[];
  providerServices?: Service[];
};

export default function Providers() {
  const { triggerRefresh } = useApp();
  const { success, error } = useToastNotifications();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState<Provider[]>([]);
  const [total, setTotal] = useState(0);
  const [deletedTotal, setDeletedTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [restoringId, setRestoringId] = useState<string | number | null>(null);
  const showDeleted = searchParams.get('view') === 'deleted';
  const [isExporting, setIsExporting] = useState(false);
  

  // Sorting functionality (including registration date)
  const [sortField, setSortField] = useState<
    | 'name'
    | 'email'
    | 'status'
    | 'createdAt'
    | 'featured'
    | 'totalServices'
    | 'averageRating'
    | null
  >(null);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Status filter options
  const statusOptions = [
    { label: 'All Status', value: 'all' },
    { label: 'Active', value: 'active', color: 'text-green-600' },
    { label: 'Inactive', value: 'inactive', color: 'text-red-600' },
  ];

  const [isLoading, setIsLoading] = useState(true);

  // State for verify confirmation modal
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [providerToVerify, setProviderToVerify] = useState<Provider | ProviderDetail | null>(null);
  const [verifyAction, setVerifyAction] = useState<'verify' | 'unverify' | null>(null);

  // State for delete confirmation modal
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [providerToDelete, setProviderToDelete] = useState<Provider | ProviderDetail | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isRestoreModalOpen, setIsRestoreModalOpen] = useState(false);
  const [providerToRestore, setProviderToRestore] = useState<Provider | ProviderDetail | null>(null);

  // Local toast management
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
    visible: boolean;
  }>({ message: '', type: 'success', visible: false });

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type, visible: true });
    setTimeout(() => {
      setToast((prev) => ({ ...prev, visible: false }));
    }, 3000);
  };

  // State for customer details modal
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState<any>(null);

  /* ------------------------------ FETCH PROVIDERS ------------------------------ */
  const fetchProviders = useCallback(async () => {
    setIsLoading(true);
    try {
      const statusParam = !showDeleted && statusFilter !== 'all' ? statusFilter : undefined;
      const searchParam = showDeleted ? undefined : search;
      const shortBy = sortField === 'featured' ? 'isFeatured' : sortField || undefined;
      const shortType = sortOrder || undefined;

      const data = await realAPI.providers.getList(
        page,
        ITEMS_PER_PAGE,
        searchParam,
        statusParam,
        shortBy,
        shortType,
        true,
        showDeleted
      );
      if (!data) {
        setProviders([]);
        setTotal(0);
        return;
      }
      const adaptedProviders = data.results.map(adaptRealProvider);

      setProviders(adaptedProviders as any);
      setTotal(data.total);
      setDeletedTotal(data.deletedCount || 0);
    } catch (error: any) {
      console.error('Failed to fetch providers:', error);
      if (error.message?.includes('404') || error.status === 404) {
        setProviders([]);
        setTotal(0);
      }
    } finally {
      setIsLoading(false);
    }
  }, [page, search, showDeleted, statusFilter, sortField, sortOrder]);

  useEffect(() => {
    fetchProviders();
  }, [fetchProviders]);

  /* ------------------------------ SEARCH HANDLER ------------------------------ */
  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setSearch(value);
      setPage(1);
    }, 300),
    []
  );

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    debouncedSearch(value);
  };

  const handleToggleDeletedView = () => {
    const params = new URLSearchParams(searchParams);
    if (showDeleted) {
      params.delete('view');
    } else {
      params.set('view', 'deleted');
    }
    setSearchParams(params);
    setPage(1);
    setSearch('');
    setSearchInput('');
    setStatusFilter('all');
  };

  const handleGoToProviders = () => {
    const params = new URLSearchParams(searchParams);
    params.delete('view');
    setSearchParams(params);
    setPage(1);
  };

  /* ------------------------------ UPDATE STATUS ------------------------------ */
  const handleToggleStatus = (provider: Provider | ProviderDetail) => {
    const newStatus = provider.status === 'active' ? 'inactive' : 'active';

    // 1. Show immediate success feedback
    success(`Provider status updated to ${newStatus}`, 'Status change applied successfully');

    // 2. Update UI instantly
    setProviders((prev) =>
      prev.map((p) => (p.id === provider.id ? { ...p, status: newStatus } : p))
    );

    // 3. Background API update (silent, no additional toast)
    realAPI.providers.updateProvider(provider.id as any, { status: newStatus }).catch((err) => {
      console.error('Failed to update status:', err);
      showToast('Failed to update status on server. Reverting change.', 'error');

      // 4. Revert UI on error
      setProviders((prev) =>
        prev.map((p) =>
          p.id === provider.id ? { ...p, status: provider.status as 'active' | 'inactive' } : p
        )
      );
    });
  };

  const handleToggleFeatured = (provider: Provider | ProviderDetail) => {
    const newValue = provider.isFeatured === 'yes' ? 'no' : 'yes';
    const isNowFeatured = newValue === 'yes';

    // 1. Show immediate success feedback
    success(
      `Provider ${isNowFeatured ? 'added to featured' : 'removed from featured'}`,
      `Provider ${isNowFeatured ? 'successfully added to featured list' : 'successfully removed from featured list'}`
    );

    // 2. Instantly update UI
    setProviders((prev) =>
      prev.map((p) => (p.id === provider.id ? { ...p, isFeatured: newValue } : p))
    );

    // 3. Background API call (silent, no additional toast)
    realAPI.providers.updateProvider(provider.id, { isFeatured: newValue }).catch((err) => {
      console.error('Failed to update featured:', err);
      const isNowFeatured = newValue === 'yes';
      error(
        `Failed to ${isNowFeatured ? 'add to featured' : 'remove from featured'}`,
        'The change has been reverted'
      );

      // 4. Revert if API fails
      setProviders((prev) =>
        prev.map((p) => (p.id === provider.id ? { ...p, isFeatured: provider.isFeatured } : p))
      );
    });
  };

  /* ------------------------------ CONFIRM VERIFY ------------------------------ */
  const handleConfirmVerify = async () => {
    if (!providerToVerify || !verifyAction) return;

    try {
      const isVerified = verifyAction === 'verify';
      const updates: any = { isVerified: isVerified };

      // If un-verifying, also deactivate if currently active
      if (!isVerified && providerToVerify.status === 'active') {
        updates.status = 'inactive';
      }

      await realAPI.providers.updateProvider(providerToVerify.id as any, updates);
      await fetchProviders();
      triggerRefresh();
      setIsVerifyModalOpen(false);
      setProviderToVerify(null);
      setVerifyAction(null);

      showToast(`Provider ${isVerified ? 'verified' : 'unverified'} successfully`, 'success');
    } catch (error) {
      console.error('Failed to update provider verified status:', error);
      showToast('Failed to update provider verified status. Please try again.', 'error');
    }
  };

  /* ------------------------------ DELETE PROVIDER ------------------------------ */
  const handleDeleteProvider = (provider: Provider | ProviderDetail) => {
    setProviderToDelete(provider);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!providerToDelete) return;

    setIsDeleting(true);

    try {
      await realAPI.users.deleteUser(providerToDelete.id as any);
      await fetchProviders();
      triggerRefresh();
      setIsDeleteModalOpen(false);
      setProviderToDelete(null);
    } catch (error) {
      console.error('Failed to delete provider:', error);
      alert('Failed to delete provider. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRestoreProvider = async (provider: Provider | ProviderDetail) => {
    setRestoringId(provider.id);
    try {
      await realAPI.users.restoreUser(provider.id as any);
      success('Provider restored successfully');
      setProviders((prev) => prev.filter((p) => p.id !== provider.id));
      setTotal((prev) => Math.max(0, prev - 1));
      setDeletedTotal((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to restore provider:', err);
      error('Failed to restore provider', 'Please try again.');
    } finally {
      setRestoringId(null);
    }
  };

  const handleOpenRestoreModal = (provider: Provider | ProviderDetail) => {
    setProviderToRestore(provider);
    setIsRestoreModalOpen(true);
  };

  const handleConfirmRestore = async () => {
    if (!providerToRestore) return;
    await handleRestoreProvider(providerToRestore);
    setIsRestoreModalOpen(false);
    setProviderToRestore(null);
  };

  /* ------------------------------ VIEW PROVIDER (detailed) ------------------------------ */
  const handleViewProvider = (provider: Provider) => {
    navigate(`/providers/${provider.id}${showDeleted ? '?view=deleted' : ''}`);
  };

  const totalPages = Math.ceil(total / ITEMS_PER_PAGE);

  /* ------------------------------ EXPORT PROVIDERS ------------------------------ */
  const handleExportProviders = async () => {
    try {
      setIsExporting(true);
      const blob = await realAPI.providers.exportProviders();
      if (!blob) return;

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'providers.csv';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);

      success('Providers exported successfully', 'The CSV file has been downloaded');
    } catch (err) {
      console.error('Failed to export providers:', err);
      error('Failed to export providers', 'Please try again later');
    } finally {
      setIsExporting(false);
    }
  };

  const PROVIDERS_TABLE_GRID = 'grid-cols-[2.1fr,2.8fr,1fr,0.9fr,0.9fr,1.1fr,0.9fr,1.2fr]';

  // Table shimmer component
  const TableShimmer = () => (
    <div className="table-container">
      <div className="table-min-w">
        {/* Real Table Headers without sorting icons */}
        <div className={`grid ${PROVIDERS_TABLE_GRID} gap-4 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm`}>
          <div className="text-gray-700 dark:text-white">Name</div>
          <div className="text-gray-700 dark:text-white">Email</div>
          <div className="text-gray-700 dark:text-white text-center">Total Services</div>
          <div className="text-gray-700 dark:text-white text-center">Rating</div>
          <div className="text-gray-700 dark:text-white text-center">Featured</div>
          <div className="text-gray-700 dark:text-white">Registration Date</div>
          <div className="text-gray-700 dark:text-white text-center">Status</div>
          <div className="text-gray-700 dark:text-white text-center">Actions</div>
        </div>

        {/* Loader in table area */}
        <div className="flex flex-col items-center justify-center py-12">
          <div className="loader"></div>
        </div>
      </div>
    </div>
  );

  /* ---------------------------------- RENDER ---------------------------------- */

  return (
    <div>
      {/* Header */}
      {/* Header */}
      <Card className="mb-2 p-[10px]">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold text-gray-900 mb-4 sm:mb-0">Service Providers</h1>

          <div className="flex flex-col sm:flex-row gap-4 w-full sm:w-auto">
                        {!showDeleted && (
                          <div className="flex items-center">
                            <Button
                              variant="outline"
                              size="sm"
                              leftIcon={<FileDown className="w-4 h-4" />}
                              onClick={handleExportProviders}
                              disabled={isExporting}
                            >
                              {isExporting ? 'Exporting...' : 'Export Providers'}
                            </Button>
                          </div>
                        )}

            <div className="flex items-center">
              <button
                onClick={handleToggleDeletedView}
                className="flex items-center gap-3 px-6 h-10 rounded-xl text-base font-medium shadow-sm w-full sm:w-auto justify-center transition-colors bg-red-100 text-red-700"
              >
                <Trash2 className="w-4 h-4" />
                <span>Deleted Providers : {deletedTotal}</span>
              </button>
            </div>

            {showDeleted && (
              <Button variant="secondary" onClick={handleGoToProviders} className="px-4 h-10">
                Go to Providers
              </Button>
            )}

            {!showDeleted && (
              <div className="flex items-center">
                <div
                  className="flex items-center gap-3 bg-gray-100 px-6 h-10
        rounded-xl text-gray-800 text-base font-medium 
        shadow-sm w-full sm:w-auto justify-center"
                >
                  <span>Total Providers : {total}</span>
                </div>
              </div>
            )}

            {!showDeleted && (
              <>
                {/* Search */}
                <div className="relative w-full sm:w-64 h-10">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by name or email"
                    value={searchInput}
                    onChange={(e) => handleSearchChange(e.target.value)}
                    className="w-full h-full pl-10 pr-4 border border-gray-300 rounded-lg 
                 focus:outline-none focus:ring-2 focus:ring-primary-500"
                  />
                </div>

                {/* Status Filter */}
                <div
                  className={`relative flex-shrink-0 transition-all duration-200
    ${!statusFilter || statusFilter === 'all' ? 'w-[140px]' : 'w-[162px]'}`}
                >
                  <Listbox
                    value={statusFilter}
                    onChange={(v) => {
                      setStatusFilter(v as 'all' | 'active' | 'inactive');
                      setPage(1);
                    }}
                  >
                    <Listbox.Button
                      className="w-full h-10 pl-4 pr-10 text-left bg-white border border-gray-300 
      rounded-lg shadow-sm focus:outline-none focus:ring-2 
      focus:ring-primary-500 transition-all duration-200 hover:border-gray-400 
      cursor-pointer flex items-center overflow-hidden"
                    >
                      <span className="block truncate">
                        {statusOptions.find((o) => o.value === statusFilter)?.label || 'Status'}
                      </span>

                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    </Listbox.Button>

                    <Listbox.Options className="absolute z-20 mt-2 w-full bg-white rounded-lg shadow-lg border border-gray-200">
                      {statusOptions.map((option) => (
                        <Listbox.Option
                          key={option.value}
                          value={option.value}
                          className={({ active }) =>
                            `px-4 py-2 cursor-pointer transition-colors ${active ? 'bg-gray-100' : ''}`
                          }
                        >
                          <span className={option.color}>{option.label}</span>
                        </Listbox.Option>
                      ))}
                    </Listbox.Options>
                  </Listbox>
                </div>

                {/* Reset Filters */}
                <Button
                  variant="secondary"
                  onClick={() => {
                    setStatusFilter('all');
                    setSortField('createdAt');
                    setSortOrder('desc');
                    setSearch('');
                    setSearchInput('');
                    setPage(1);
                    success('All filters reset', 'Filters have been cleared');
                  }}
                  className="px-4 h-10"
                  style={{ width: '162px' }}
                >
                  Reset Filters
                </Button>
              </>
            )}
          </div>
        </div>
      </Card>
      {/* Table wrapper */}
      <Card>
        {isLoading ? (
          <TableShimmer />
        ) : providers.length === 0 ? (
          <div className="table-container">
            <div className="table-min-w">
              {/* Grid Header */}
              <div className={`grid ${PROVIDERS_TABLE_GRID} gap-4 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm`}>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('name');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>

                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'email') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('email');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  <div>Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}</div>
                </div>

                <div
                  className="text-center cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'totalServices') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('totalServices');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  <div className="text-center">
                    Total Services{' '}
                    {sortField === 'totalServices' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>

                <div
                  className="text-center cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'averageRating') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('averageRating');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  <div className="text-center">
                    Rating {sortField === 'averageRating' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                </div>

                <div
                  className="text-center cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'featured') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('featured');
                      setSortOrder('desc'); // Usually want to see featured first (descending 1 > 0)
                    }
                    setPage(1);
                  }}
                >
                  <div>
                    Featured {sortField === 'featured' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>
                  {/* <Star className="w-4 h-4 text-yellow-500 mx-auto mt-1" /> */}
                </div>

                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'createdAt') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('createdAt');
                      setSortOrder('desc');
                    }
                    setPage(1);
                  }}
                >
                  Registration Date {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>

                <div
                  className="cursor-pointer text-center hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'status') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                    } else {
                      setSortField('status');
                      setSortOrder('asc');
                    }
                    setPage(1);
                  }}
                >
                  Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                </div>

                <div className="text-center">Actions</div>
              </div>
              {/* Empty State inside table */}
              <div className="text-center py-12 bg-gray-50 rounded-b-xl">
                <img src="/NoRecord.svg" alt="No records found" className=" mx-auto mb-4 " />
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="table-container">
              <div className="table-min-w">
                {/* Grid Header */}
              <div className={`grid ${PROVIDERS_TABLE_GRID} gap-4 p-4 pl-3 rounded-t-xl font-semibold text-gray-700 text-[15px] border-b border-gray-200 -ml-2 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 dark:text-white dark:border-gray-600 shadow-sm`}>
                <div
                  className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                  onClick={() => {
                    if (sortField === 'name') {
                      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('name');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Name {sortField === 'name' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'email') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('email');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    <div>Email {sortField === 'email' && (sortOrder === 'asc' ? '↑' : '↓')}</div>
                  </div>

                  <div
                    className="text-center cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'totalServices') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('totalServices');
                        setSortOrder('desc');
                      }
                      setPage(1);
                    }}
                  >
                    <div className="text-center">
                      Total Services{' '}
                      {sortField === 'totalServices' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </div>

                  <div
                    className="text-center cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'averageRating') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('averageRating');
                        setSortOrder('desc');
                      }
                      setPage(1);
                    }}
                  >
                    <div className="text-center">
                      Rating {sortField === 'averageRating' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                  </div>

                  <div
                    className="text-center cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'featured') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('featured');
                        setSortOrder('desc'); // Usually want to see featured first (descending 1 > 0)
                      }
                      setPage(1);
                    }}
                  >
                    <div>
                      Featured {sortField === 'featured' && (sortOrder === 'asc' ? '↑' : '↓')}
                    </div>
                    {/* <Star className="w-4 h-4 text-yellow-500 mx-auto mt-1" /> */}
                  </div>

                  <div
                    className="cursor-pointer hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'createdAt') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('createdAt');
                        setSortOrder('desc');
                      }
                      setPage(1);
                    }}
                  >
                    Registration Date{' '}
                    {sortField === 'createdAt' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div
                    className="cursor-pointer text-center hover:text-primary-600 transition-colors dark:hover:text-primary-400 dark:text-white"
                    onClick={() => {
                      if (sortField === 'status') {
                        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
                      } else {
                        setSortField('status');
                        setSortOrder('asc');
                      }
                      setPage(1);
                    }}
                  >
                    Status {sortField === 'status' && (sortOrder === 'asc' ? '↑' : '↓')}
                  </div>

                  <div className="text-center">Actions</div>
                </div>

                {/* Grid Body */}
                <div className="divide-y divide-gray-200 dark:divide-gray-700 -ml-2">
                  {providers.map((provider) => {
                    const providerImageSrc = getUserImageSrc(
                      provider.image,
                      provider.signInProvider
                    );
                    return (
                      <div
                        key={provider.id}
                        className={`grid ${PROVIDERS_TABLE_GRID} 
             gap-4 p-[0.4rem] pl-[0.4rem] 
             hover:bg-blue-50 dark:hover:bg-gray-700/50 
             transition-colors items-center cursor-pointer`}
                        onClick={() => handleViewProvider(provider)}
                      >
                        <div className="flex items-center gap-2 truncate">
                          {providerImageSrc ? (
                            <img
                              src={providerImageSrc}
                              alt={(provider as any).name}
                              className="w-10 h-10 rounded-lg object-cover flex-shrink-0"
                              onError={(e) => {
                                console.warn('Provider image failed to load:', provider.image);
                                e.currentTarget.src = '/logo192.png';
                              }}
                              onLoad={() => {}}
                            />
                          ) : (
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 text-white font-bold text-xl"
                              style={{
                                backgroundColor: getAvatarBackgroundColor((provider as any).name),
                              }}
                            >
                              {getUserInitial((provider as any).name, 'P')}
                            </div>
                          )}
                          <span
                            className="capitalize truncate dark:text-white"
                            title={(provider as any).name}
                          >
                            {(provider as any).name}
                          </span>
                        </div>

                        <div
                          className="truncate text-sm text-black-600 dark:text-gray-300"
                          title={(provider as any).email}
                        >
                          {(provider as any).email}
                        </div>
                        <div className="text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-md">
                            {(provider as any).totalServices || 0}
                          </span>
                        </div>
                        <div className="text-center">
                          <span className="inline-flex items-center justify-center px-3 py-1 text-sm font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200 rounded-md gap-1">
                            {Number(provider.averageRating || 0) % 1 === 0
                              ? Math.floor(provider.averageRating || 0)
                              : Number(provider.averageRating || 0).toFixed(1)}
                            <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                          </span>
                        </div>

                        <div className="flex justify-center items-center">
                          <button
                            className="w-12 h-12 flex items-center justify-center rounded-lg transition-colors mx-auto disabled:cursor-not-allowed disabled:opacity-60"
                            disabled={showDeleted}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!showDeleted) {
                                handleToggleFeatured(provider);
                              }
                            }}
                          >
                            {provider.isFeatured === 'yes' ? (
                              <Star className="w-7 h-7 fill-yellow-500 stroke-[2] stroke-yellow-500" />
                            ) : (
                              <Star className="w-7 h-7 stroke-[2] text-yellow-500 fill-transparent" />
                            )}
                          </button>
                        </div>
                        <div className="text-sm text-black-600 dark:text-gray-300">
                          {(provider as any).createdAt
                            ? formatDate((provider as any).createdAt)
                            : 'N/A'}
                        </div>

                        <div className="flex justify-center">
                          <span
                            className={`inline-flex px-2 py-1 text-xs font-semibold rounded-md capitalize ${
                              provider.status === 'active'
                                ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                            }`}
                          >
                            {provider.status}
                          </span>
                        </div>

                        <div>
                          <div className="flex justify-center gap-3">
                            {showDeleted ? (
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenRestoreModal(provider);
                                }}
                                disabled={restoringId === provider.id}
                                className="px-3 py-1 text-sm rounded-md bg-green-100 text-green-700 hover:bg-green-200 disabled:opacity-60"
                              >
                                {restoringId === provider.id ? 'Restoring...' : 'Restore'}
                              </button>
                            ) : (
                              <>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleToggleStatus(provider);
                                  }}
                                  className={`p-2 rounded-lg transition-colors ${
                                    provider.status === 'active'
                                      ? 'text-green-600 hover:bg-green-50'
                                      : 'text-red-600 hover:bg-red-50'
                                  }`}
                                >
                                  {provider.status === 'active' ? (
                                    <ToggleRight className="w-7 h-7" />
                                  ) : (
                                    <ToggleLeft className="w-7 h-7" />
                                  )}
                                </button>

                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDeleteProvider(provider);
                                  }}
                                  className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  title="Delete Provider"
                                >
                                  <Trash2 className="w-6 h-6" />
                                </button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-6 pt-6 border-t border-gray-200 dark:border-gray-600">
                <p className="text-sm text-gray-600 dark:text-gray-300">
                  Showing {(page - 1) * ITEMS_PER_PAGE + 1} to{' '}
                  {Math.min(page * ITEMS_PER_PAGE, total)} of {total} results
                </p>

                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                  >
                    <ChevronLeft className="w-4 h-4" />
                  </Button>

                  <span className="px-4 py-2 text-sm font-medium text-gray-700">
                    Page {page} of {totalPages}
                  </span>

                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {/* Verify Confirmation Modal */}
      <Modal
        isOpen={isVerifyModalOpen}
        onClose={() => {
          setIsVerifyModalOpen(false);
          setProviderToVerify(null);
          setVerifyAction(null);
        }}
        title="Confirm Provider Verification"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to {verifyAction} this provider?
            {verifyAction === 'unverify' && providerToVerify?.status === 'active' && (
              <span className="block mt-2 text-orange-600 font-medium">
                Note: Un-verifying will also deactivate this provider.
              </span>
            )}
          </p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsVerifyModalOpen(false);
                setProviderToVerify(null);
                setVerifyAction(null);
              }}
            >
              Cancel
            </Button>
            <Button
              variant={verifyAction === 'verify' ? 'primary' : 'danger'}
              onClick={handleConfirmVerify}
            >
              {verifyAction === 'verify' ? 'Verify' : 'Unverify'} Provider
            </Button>
          </div>
        </div>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => {
          setIsDeleteModalOpen(false);
          setProviderToDelete(null);
        }}
        title="Delete Provider"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">
            Are you sure you want to delete this provider? This action cannot be undone.
          </p>
          <div className="bg-red-50 p-3 rounded-lg">
            <p className="text-sm text-red-800">
              <strong>Warning:</strong> Deleting this provider will permanently remove all their
              data, including services, reviews, and booking history.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsDeleteModalOpen(false);
                setProviderToDelete(null);
              }}
            >
              Cancel
            </Button>
            <Button variant="danger" onClick={handleConfirmDelete} disabled={isDeleting}>
              {isDeleting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Deleting
                </>
              ) : (
                'Delete Provider'
              )}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Restore Confirmation Modal */}
      <Modal
        isOpen={isRestoreModalOpen}
        onClose={() => {
          if (!restoringId) {
            setIsRestoreModalOpen(false);
            setProviderToRestore(null);
          }
        }}
        title="Restore Provider"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-gray-700">Are you sure you want to restore this provider?</p>
          <div className="flex gap-3 justify-end">
            <Button
              variant="secondary"
              onClick={() => {
                setIsRestoreModalOpen(false);
                setProviderToRestore(null);
              }}
              disabled={!!restoringId}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleConfirmRestore} disabled={!!restoringId}>
              {restoringId ? 'Restoring...' : 'Restore Provider'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Customer Details Modal */}
      <Modal
        isOpen={isCustomerModalOpen}
        onClose={() => {
          setIsCustomerModalOpen(false);
          setSelectedCustomer(null);
        }}
        title="Customer Profile"
        size="2xl" // wide modal
      >
        <div className="max-w-6xl w-full mx-auto">
          {' '}
          {/* ⬅️ Increased width here */}
          {selectedCustomer ? (
            <div className="space-y-6">
              {/* Customer Basic Info */}
              <div className="flex items-center gap-4">
                {getUserImageSrc(selectedCustomer.image, selectedCustomer.signInProvider) ? (
                  <img
                    src={getUserImageSrc(selectedCustomer.image, selectedCustomer.signInProvider)!}
                    alt={selectedCustomer.name}
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-xl font-semibold"
                    style={{ backgroundColor: getAvatarBackgroundColor(selectedCustomer.name) }}
                  >
                    {getUserInitial(selectedCustomer.name, 'U')}
                  </div>
                )}
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedCustomer.name || 'Unknown User'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {selectedCustomer.username ? `@${selectedCustomer.username}` : 'N/A'}
                  </p>
                </div>
              </div>

              {/* Customer Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email Address</label>
                    <p className="text-gray-900">{selectedCustomer.email || 'Not provided'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Mobile Number</label>
                    <p className="text-gray-900">
                      {selectedCustomer.countryCode && selectedCustomer.mobileNumber
                        ? (() => {
                            const code = selectedCustomer.countryCode.trim();
                            // Remove existing + if present
                            const cleanCode = code.startsWith('+') ? code.substring(1) : code;
                            return `+${cleanCode} ${selectedCustomer.mobileNumber}`;
                          })()
                        : selectedCustomer.mobileNumber || selectedCustomer.phone || 'Not provided'}
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Member Since</label>
                    <p className="text-gray-900">
                      {selectedCustomer.createdAt
                        ? formatDate(selectedCustomer.createdAt)
                        : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Status</label>
                    <p>
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          selectedCustomer.status === 'active'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-red-100 text-red-800'
                        }`}
                      >
                        {selectedCustomer.status || 'Unknown'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Bio */}
              {selectedCustomer.bio && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Bio</label>
                  <p className="text-gray-900 mt-1">{selectedCustomer.bio}</p>
                </div>
              )}

              {/* Additional Fields */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">User ID</label>
                    <p className="text-gray-900 text-sm">{selectedCustomer.id || 'Not provided'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Role</label>
                    <p className="text-gray-900">{selectedCustomer.role || 'Not provided'}</p>
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-500">Email Verified</label>
                    <p className="text-gray-900">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          selectedCustomer.emailVerified === 'yes'
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {selectedCustomer.emailVerified || 'Unknown'}
                      </span>
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Address</label>
                    <p className="text-gray-900">
                      {selectedCustomer.location ? selectedCustomer.location : 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Online Status</label>
                    <p className="text-gray-900">
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                          selectedCustomer.isOnline
                            ? 'bg-green-100 text-green-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {selectedCustomer.isOnline ? 'Online' : 'Offline'}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex justify-end pt-4 border-t">
                <Button
                  variant="secondary"
                  onClick={() => {
                    setIsCustomerModalOpen(false);
                    setSelectedCustomer(null);
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 rounded w-1/2 mx-auto mb-4"></div>
                <div className="h-8 bg-gray-200 rounded w-3/4 mx-auto"></div>
              </div>
            </div>
          )}
        </div>
      </Modal>

      {/* Local Toast Component */}
      {toast.visible && (
        <div
          className={`fixed top-4 right-4 px-6 py-3 rounded-lg shadow-lg z-50 transform transition-all duration-300 ${
            toast.type === 'success' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
          }`}
        >
          <div className="flex items-center gap-2">
            {toast.type === 'success' ? (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            ) : (
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            )}
            <span className="font-medium">{toast.message}</span>
          </div>
        </div>
      )}
    </div>
  );
}
